import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Loader2, Activity } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Human-readable phrasing for canonical product events.
const VERB = {
  user_registered: 'Registered an account',
  user_login: 'Logged in',
  session_ended: 'Logged out',
  resume_updated: 'Updated their resume',
  resume_completed: 'Completed their resume',
  profile_visibility_enabled: 'Made their profile visible',
  opportunity_viewed: 'Viewed an opportunity',
  opportunity_saved: 'Saved an opportunity',
  application_started: 'Started an application',
  application_submitted: 'Submitted an application',
  application_contacted: 'Contacted a provider',
  application_withdrawn: 'Withdrew an application',
  application_hired: 'Marked a candidate hired',
  job_published: 'Published a job',
  resume_access_requested: 'Requested resume access',
  application_reviewed: 'Reviewed an application',
  conversation_started: 'Started a conversation',
};

const EVENT_FILTERS = ['all', ...Object.keys(VERB)];
const ROLE_FILTERS = ['all', 'job_seeker', 'employer', 'regional_admin', 'admin'];

function phrase(e) {
  let base = VERB[e.event_name] || e.event_name;
  const m = e.metadata || {};
  if (e.event_name === 'opportunity_viewed' || e.event_name === 'opportunity_saved') {
    if (m.opportunity_type) base += ` (${m.opportunity_type})`;
  }
  return base;
}

export default function AdminActivity() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [eventName, setEventName] = useState('all');
  const [role, setRole] = useState('all');

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (eventName !== 'all') params.append('event_name', eventName);
      if (role !== 'all') params.append('role', role);
      params.append('limit', '100');
      const r = await axios.get(`${BACKEND_URL}/api/admin/activity?${params.toString()}`, { withCredentials: true });
      setEvents(r.data || []);
    } catch { setEvents([]); } finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [eventName, role]);

  return (
    <div className="space-y-4" data-testid="admin-activity">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-500">Event</span>
          <select data-testid="activity-event-filter" value={eventName} onChange={(e) => setEventName(e.target.value)}
            className="h-9 rounded-md border border-slate-300 bg-white px-2 text-sm">
            {EVENT_FILTERS.map(k => <option key={k} value={k}>{k === 'all' ? 'All events' : (VERB[k] || k)}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-500">Role</span>
          <select data-testid="activity-role-filter" value={role} onChange={(e) => setRole(e.target.value)}
            className="h-9 rounded-md border border-slate-300 bg-white px-2 text-sm">
            {ROLE_FILTERS.map(k => <option key={k} value={k}>{k === 'all' ? 'All roles' : k}</option>)}
          </select>
        </div>
        <span className="text-xs text-slate-400 ml-auto">Canonical product activity (since tracking began)</span>
      </div>

      <Card className="border border-slate-200">
        {loading ? (
          <div className="p-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-brand" /></div>
        ) : events.length === 0 ? (
          <div className="p-10 text-center text-slate-500">
            <Activity className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            No product activity recorded yet for this filter.
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {events.map((e) => (
              <div key={e.event_id} data-testid={`activity-row-${e.event_id}`} className="p-4 flex items-start gap-3 hover:bg-slate-50">
                <div className="flex-shrink-0 w-9 h-9 rounded-full bg-brand-light flex items-center justify-center">
                  <Activity className="w-4 h-4 text-brand" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-slate-900">
                    <span className="font-semibold">{e.actor_name || 'Unknown user'}</span>
                    <span className="text-slate-400 text-xs ml-2">{e.actor_role}</span>
                  </p>
                  <p className="text-sm text-slate-600">{phrase(e)}{e.entity_type ? <span className="text-slate-400"> · {e.entity_type} {e.entity_id ? String(e.entity_id).slice(0, 16) : ''}</span> : null}</p>
                </div>
                <div className="text-xs text-slate-400 whitespace-nowrap">{e.timestamp ? new Date(e.timestamp).toLocaleString() : ''}</div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
